<template>
  <div class="content-alumni-event-list">
    <div v-for="event in eventList" :key="event.id" class="event-card">
      <img v-if="event.imageUrl" :src="event.imageUrl" alt="" class="event-image" />
      <div class="content">
        <h2 class="title text-2xl">{{ event.name }}</h2>
        <div class="location-date-wrapper">
          <div v-if="event.location" class="location">
            <SvgLocation class="icon" aria-hidden="true" />{{ event.location }}
          </div>
          <div class="date">
            <SvgCalendar class="icon" aria-hidden="true" />
            <template v-if="event.allDay">
              {{ formatAllDayDateRange(event.start, event.end) }}
            </template>
            <template v-else>{{ formatEventDateRange(event.start, event.end) }}</template>
          </div>
        </div>
        <div v-if="event.description" class="description" v-html="event.description" />
        <div v-if="loggedIn" class="actions">
          <button
            v-if="event.user.participationState !== 'attending'"
            class="button-secondary"
            :disabled="isUpdatingParticipation"
            @click="changeParticipation(event.id, 'attend', event.user.participationId)"
          >
            {{ t('participate') }}
          </button>
          <div v-if="event.user.participationState === 'attending'" class="attending-container">
            <span class="attending-state-chip copy-regular">
              <SvgCheckCircle aria-hidden="true" />
              {{ t('attending') }}
            </span>
            <button
              class="button-link"
              :disabled="isUpdatingParticipation"
              @click="changeParticipation(event.id, 'reject', event.user.participationId)"
            >
              {{ t('reject') }}
            </button>
          </div>
        </div>
        <div v-else class="actions">
          <a v-if="event.user.participationState !== 'attending'" class="button-secondary" :href="loginPageUrl">
            {{ t('participate') }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<i18n lang="yaml">
de:
  participate: Teilnehmen
  reject: Abmelden
  attending: Angemeldet
en:
  participate: Participate
  reject: Reject
  attending: Registered
</i18n>

<script setup lang="ts">
import SvgLocation from '~/components/content-modules/svgs/SvgLocation.vue';
import SvgCalendar from '~/components/content-modules/svgs/SvgCalendar.vue';
import SvgCheckCircle from '~/components/content-modules/svgs/SvgCheckCircle.vue';
import type { ContentAlumniEventListFragment } from '#gql';
import { useAlumniSettings } from '~/app/useAlumniSettings';

const props = defineProps<{
  data: ContentAlumniEventListFragment;
}>();

const { loginPageUrl } = useAlumniSettings();

const { data: eventList, refresh } = useFetch('/api/alumni/events', {
  query: {
    count: props.data.showOnlyNextUpcomingEvent ? 1 : 15,
  },
});

const { loggedIn } = useUserSession();

const { t, locale } = useI18n();

const isUpdatingParticipation = ref(false);

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString(locale.value, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString(locale.value, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });
}

function formatEventDateRange(start: string, end: string): string {
  const day1 = new Date(start);
  const day2 = new Date(end);
  const sameDay =
    day1.getFullYear() === day2.getFullYear() &&
    day1.getMonth() === day2.getMonth() &&
    day1.getDate() === day2.getDate();

  if (sameDay) {
    const date = day1.toLocaleDateString(locale.value, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const startTime = day1.toLocaleTimeString(locale.value, {
      hour: '2-digit',
      minute: '2-digit',
    });
    const endTime = day2.toLocaleTimeString(locale.value, {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${date}, ${startTime} - ${endTime}`;
  }
  return `${formatDateTime(start)} - ${formatDateTime(end)}`;
}

function formatAllDayDateRange(start: string, end: string): string {
  const day1 = new Date(start);
  const day2 = new Date(end);
  const sameDay =
    day1.getFullYear() === day2.getFullYear() &&
    day1.getMonth() === day2.getMonth() &&
    day1.getDate() === day2.getDate();

  if (sameDay) {
    return formatDate(start);
  }
  return `${formatDate(start)} - ${formatDate(end)}`;
}

async function changeParticipation(eventId: number, state: 'attend' | 'reject', participationId: number | undefined) {
  isUpdatingParticipation.value = true;
  try {
    await $fetch('/api/alumni/participation', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: {
        eventId,
        state,
        participationId,
      },
    });
  } finally {
    await refresh();
    isUpdatingParticipation.value = false;
  }
}
</script>

<style scoped lang="scss">
.content-alumni-event-list {
  @include grid-layout();

  & {
    padding: var(--site-inner-padding);
    width: 100%;
    max-width: var(--site-max-width);
  }

  > .event-card {
    display: grid;

    @media (--xs), (--sm) {
      padding: var(--spacing-xs) 0;
      grid-column: 1 / -1;
      gap: var(--spacing-2xs);
    }

    @media (--md), (--lg) {
      padding: var(--spacing-2xl) 0;
      grid-column: 1 / -1;
      grid-template-columns: repeat(2, 1fr);
      gap: var(--spacing-xl);
      align-items: center;
    }

    > .event-image {
      grid-column: 1 / 2;
      width: 100%;
      aspect-ratio: 4 / 3;
      object-fit: cover;
    }

    > .content {
      grid-column: 2 / -1;
      display: grid;

      @media (--xs), (--sm) {
        gap: var(--spacing-3xs);
      }

      @media (--md), (--lg) {
        gap: var(--spacing-xs);
      }

      > .location-date-wrapper {
        display: grid;
        gap: var(--spacing-4xs);

        > .location,
        > .date {
          display: flex;

          @media (--xs), (--sm) {
            gap: var(--spacing-4xs);
          }

          @media (--md), (--lg) {
            gap: var(--spacing-3xs);
          }

          > .icon {
            @media (--lg) {
              margin-top: 0.4%;
            }
          }
        }
      }

      > .actions {
        > .attending-container {
          display: flex;
          align-items: center;
          gap: var(--spacing-3xs);

          > .attending-state-chip {
            @include pill-alumni;
          }
        }
      }
    }
  }
}
</style>
